import React from "react";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { MinimalTableCell, MinimalTableCellWithTooltip } from "src/ui/shared/table/MinimalTableComponents";
import { tagsFilter } from "src/ui/shared/table/Tanstack.utils";

import { getDefaultGroupFileParams } from "@dashboard/files/api/helpers";
import { createVersionCodeTag, FileInfoWithVersions, hasApkMetadata } from "@dashboard/files/utils";
import { getFileSourceKey, sourceConfig } from "@dashboard/files/utils/fileSource";
import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { createRolloutTag } from "@dashboard/shared/tags";
import { Box } from "@mui/material";
import UserSettingsService from "@services/userSettingsService";
import { Tooltip } from "@shared/Tooltip";
import { ColumnDef } from "@tanstack/react-table";

import ActionsCell from "./components/ActionsCell";
import { GroupFileTabParams } from "./FileTab";
import { useStyles } from "./FileTab.style";

export const FileNameCell = ({ file }: { file: API.File }) => {
    const title = hasApkMetadata(file) ? file.metadata?.file?.apk?.manifest?.package : file.filename;
    return <MinimalTableCellWithTooltip title={title || "-"} style={{ maxWidth: 260 }} />;
};

export const SourceCell = ({ file, selectedGroupId }: { file: API.File; selectedGroupId?: number }) => {
    const source = getFileSourceKey(file.groupId, selectedGroupId);
    const { tooltip, icon } = sourceConfig[source];

    return (
        <MinimalTableCell>
            <Box>
                <Tooltip title={tooltip}>
                    <i className={`fa-solid ${icon}`} />
                </Tooltip>
            </Box>
        </MinimalTableCell>
    );
};

export const FileTabTagsCell = ({ file }: { file: API.File }) => {
    const { classes } = useStyles();

    const versionCodeTag = createVersionCodeTag(file);
    const rolloutTag = createRolloutTag(file.rolloutPercentage);
    const removableTags = file.metadata?.user?.tags;

    return (
        <MinimalTableCell>
            <div className={classes.tagsBox}>
                {removableTags?.length ? removableTags.map((item) => <Tag key={item.key} item={item} />) : null}
                {versionCodeTag ? <Tag key={versionCodeTag.key} item={versionCodeTag} theme="light" /> : null}
                {rolloutTag ? <Tag key={rolloutTag.key} item={rolloutTag} theme="light" /> : null}
            </div>
        </MinimalTableCell>
    );
};

export const columns: ColumnDef<FileInfoWithVersions, unknown>[] = [
    {
        id: "collapseButton",
        header: "",
        enableSorting: false,
        cell: () => null,
    },
    {
        header: i18next.t("common:label.packageName"),
        enableSorting: true,
        accessorKey: "filename",
        cell: (info) => <FileNameCell file={info.row.original} />,
    },
    {
        header: i18next.t("common:label.source"),
        accessorKey: "groupId",
        enableSorting: false,
        cell: (info) => <SourceCell file={info.row.original} selectedGroupId={info.table.options.meta?.selectedGroupId} />,
    },
    {
        header: i18next.t("common:label.tags"),
        accessorKey: "tags",
        enableSorting: false,
        meta: { filterVariant: "filesTags" },
        filterFn: tagsFilter,
        cell: (info) => <FileTabTagsCell file={info.row.original} />,
    },
    {
        accessorKey: "action",
        header: "",
        enableSorting: false,
        cell: (info) => {
            return (
                <MinimalTableCell style={{ overflow: "visible" }}>
                    <ActionsCell file={info.row.original} />
                </MinimalTableCell>
            );
        },
    },
];

export function useParams(groupId: number): [GroupFileTabParams, React.Dispatch<React.SetStateAction<GroupFileTabParams>>] {
    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const [storedParams, setParams] = React.useState<GroupFileTabParams>(() => {
        const base = getDefaultGroupFileParams(groupId) as GroupFileTabParams;
        if (workspaceId !== null) {
            base.workspaceId = workspaceId;
        }
        return base;
    });

    const params = React.useMemo(
        () => ({
            ...storedParams,
            groups: [groupId],
            ...(workspaceId !== null && { workspaceId }),
        }),
        [storedParams, groupId, workspaceId],
    );

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupSidePanel.fileTab.params", params);
    }, [params]);

    return [params, setParams];
}
